import { collection, query, where, orderBy, startAt, endAt, limit, startAfter, getDocs, getDoc, doc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig'; // Import your Firestore instance

/**
 * Enriches items with user-specific data by fetching details from a user's subcollection
 * for any items they have interacted with.
 *
 * @param {Array} items - The array of words or sentences from the global collection.
 * @param {string|null} userId - The current user's ID.
 * @param {'words'|'sentences'} contentType - The type of content to enrich.
 * @param {Set|null} learnedIdsSet - Optional pre-loaded Set of learned IDs from MetadataProvider.
 *   If not provided, assumes the user is not logged in and returns items as unlearned.
 * @returns {Promise<Array>} A new array of items enriched with user-specific data.
 */
export const enrichItemsWithUserData = async (items, userId, contentType, learnedIdsSet = null) => {
  // If no user, no items, or no learnedIdsSet provided, return items with default unenriched values.
  if (!userId || items.length === 0 || !learnedIdsSet) {
    return items.map((item) => ({ ...item, srs_stage: null }));
  }

  // Determine the subcollection name based on content type.
  const subcollectionName = contentType === 'words' ? 'user_words' : 'user_sentences';

  try {
    if (learnedIdsSet.size === 0) {
      return items.map((item) => ({ ...item, srs_stage: null }));
    }

    const itemsToFetchDetailsFor = items.filter((item) => learnedIdsSet.has(item.id));
    const userDetailsMap = new Map();

    if (itemsToFetchDetailsFor.length > 0) {
      const detailPromises = itemsToFetchDetailsFor.map((item) => {
        const itemDocRef = doc(firestore, 'users', userId, subcollectionName, item.id);
        return getDoc(itemDocRef);
      });
      const detailSnapshots = await Promise.all(detailPromises);
      detailSnapshots.forEach((snap) => {
        if (snap.exists()) {
          userDetailsMap.set(snap.id, snap.data());
        }
      });
    }

    return items.map((item) => {
      if (learnedIdsSet.has(item.id)) {
        const userDetails = userDetailsMap.get(item.id) || {};
        return { ...item, ...userDetails };
      } else {
        return { ...item, srs_stage: null };
      }
    });
  } catch (error) {
    console.error(`Error during user data enrichment (userId: ${userId}):`, error);
    return items.map((item) => ({ ...item, srs_stage: null }));
  }
};

/**
 * Refetches a single item with its current user progress data
 * @param {string} itemId - The ID of the item to refetch
 * @param {string} userId - The current user's ID
 * @param {'words'|'sentences'} contentType - The type of content
 * @returns {Promise<Object>} The item with updated user progress data
 */
export const refetchSingleItem = async (itemId, userId, contentType) => {
  if (!userId || !itemId) {
    throw new Error('Missing required parameters for refetchSingleItem');
  }

  try {
    const collectionName = contentType === 'words' ? 'words' : 'sentences';
    const subcollectionName = contentType === 'words' ? 'user_words' : 'user_sentences';

    // Fetch base item and user progress doc in parallel
    const [itemSnap, userProgressSnap] = await Promise.all([
      getDoc(doc(firestore, collectionName, itemId)),
      getDoc(doc(firestore, 'users', userId, subcollectionName, itemId)),
    ]);

    if (!itemSnap.exists()) {
      throw new Error(`Item ${itemId} not found in ${collectionName} collection`);
    }

    const baseItem = { id: itemSnap.id, ...itemSnap.data() };

    if (userProgressSnap.exists()) {
      return { ...baseItem, ...userProgressSnap.data() };
    }

    return { ...baseItem, srs_stage: null, skipped: false };
  } catch (error) {
    console.error(`Error refetching item ${itemId}:`, error);
    throw error;
  }
};

export const fetchSentences = async (selectedLevel, sentencesPerPage, lastVisible, userId, learnedFilter, learnedIdsSet = null, kanjiComplexityFilter = null) => {
  try {
    const sentencesRef = collection(firestore, 'sentences');
    let queryConstraints = [];

    // Add level filter if not 'all'
    if (selectedLevel !== 'all') {
      queryConstraints.push(where('level', '==', parseInt(selectedLevel)));
    }

    // Add kanji complexity filter (always equality, composite index needed in prod)
    if (kanjiComplexityFilter) {
      if (kanjiComplexityFilter.val === -1) {
        return { sentences: [], lastVisible: null, hasMore: false };
      }
      queryConstraints.push(where('kanji_complexity', '==', kanjiComplexityFilter.val));
    }
    queryConstraints.push(orderBy('level', 'desc'));

    queryConstraints.push(limit(sentencesPerPage));

    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }

    const q = query(sentencesRef, ...queryConstraints);
    
    
    const snapshot = await getDocs(q);
    

    let fetchedSentences = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // --- ENRICHMENT STEP (REFACTORED) ---
    // Use the new, highly efficient enrichment function.
    let enrichedSentences = await enrichItemsWithUserData(fetchedSentences, userId, 'sentences', learnedIdsSet);

    // Client-side filtering logic remains the same and is correct.
    if (learnedFilter === 'learned') {
      enrichedSentences = enrichedSentences.filter((s) => learnedIdsSet?.has(s.id));
    } else if (learnedFilter === 'not-learned') {
      enrichedSentences = enrichedSentences.filter((s) => !learnedIdsSet?.has(s.id));
    }

    const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
    let hasMore = snapshot.docs.length === sentencesPerPage;
    
    
    // If client-side filtering resulted in 0 items but we fetched full page,
    // we need to continue loading to find filtered items
    if (enrichedSentences.length === 0 && hasMore) {
      // Keep hasMore as true to continue loading
      // Update lastVisible to move to next page even though no items matched filter
    } else if (enrichedSentences.length === 0 && !hasMore) {
      // No more items in database and none match filter
      hasMore = false;
    }

    return {
      sentences: enrichedSentences,
      lastVisible: newLastVisible,
      hasMore,
    };
  } catch (error) {
    console.error('Error fetching sentences:', error);
    throw error;
  }
};

export const fetchWords = async (selectedLevel, wordsPerPage, lastVisible, userId, learnedFilter, learnedIdsSet = null, grammarFilter = false) => {
  try {
    const wordsRef = collection(firestore, 'words');
    let queryConstraints = [];

    // Add level filter if not 'all'
    if (selectedLevel !== 'all') {
      queryConstraints.push(where('level', '==', parseInt(selectedLevel)));
    }
    
    // Add grammar filter if enabled
    if (grammarFilter) {
      queryConstraints.push(where('tags', 'array-contains', 'grammar'));
    }
    
    // Add ordering and pagination
    queryConstraints.push(orderBy('level', 'desc'));
    queryConstraints.push(limit(wordsPerPage));
    
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }

    const q = query(wordsRef, ...queryConstraints);
    const snapshot = await getDocs(q);

    let fetchedWords = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // --- ENRICHMENT STEP (REFACTORED) ---
    // Use the new, highly efficient enrichment function.
    let enrichedWords = await enrichItemsWithUserData(fetchedWords, userId, 'words', learnedIdsSet);

    // Client-side filtering logic remains the same and is correct.
    if (learnedFilter === 'learned') {
      enrichedWords = enrichedWords.filter((w) => learnedIdsSet?.has(w.id));
    } else if (learnedFilter === 'not-learned') {
      enrichedWords = enrichedWords.filter((w) => !learnedIdsSet?.has(w.id));
    }

    const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
    let hasMore = snapshot.docs.length === wordsPerPage;
    
    // If client-side filtering resulted in 0 items but we fetched full page,
    // we need to continue loading to find filtered items
    if (enrichedWords.length === 0 && hasMore) {
      // Keep hasMore as true to continue loading
    } else if (enrichedWords.length === 0 && !hasMore) {
      // No more items in database and none match filter
      hasMore = false;
    }

    return {
      words: enrichedWords,
      lastVisible: newLastVisible,
      hasMore,
    };
  } catch (error) {
    console.error('Error fetching words:', error);
    throw error;
  }
};

export const fetchSentenceWords = async (words) => {
  try {
    const wordsWithDetails = await Promise.all(
      words.map(async (word) => {
        let wordDoc;
        if (word.word_id) {
          // Fetch the document directly if word_id exists
          wordDoc = await getDoc(word.word_id);
        } else if (word.id) {
          // If word_id doesn't exist, look up by word.id in the "words" collection
          const wordDocRef = doc(firestore, 'words', word.id);
          wordDoc = await getDoc(wordDocRef);
        }
        if (wordDoc.exists()) {
          return {
            ...word,
            ...wordDoc.data(), // Merge fetched word data with the original word object
            id: wordDoc.id,
          };
        } else {
          console.error('Word not found in Firestore');
          return word; // Return the original word object if the fetch fails
        }
      })
    );

    return wordsWithDetails; // Return the updated list of words with details
  } catch (error) {
    console.error('Error fetching word details:', error);
    throw error; // Throw error for error handling in the calling component
  }
};

/**
 * Searches for words or sentences in Firestore.
 * - For words, can search by 'kana' (prefix) or 'kanji' (prefix on the 'word' field).
 * - For sentences, can search by 'kana' (prefix on 'sentence_kana' field) or 'kanji' (prefix on the 'sentence' field).
 * Can limit results or fetch all, and indicates if more results exist beyond the limit.
 * Adds 'learned' status to each result if userId is provided.
 * @param {string} searchQuery The term to search for.
 * @param {'words'|'sentences'} searchType Type of item to search.
 * @param {'kana'|'kanji'} [searchBy='kana'] Field/method to search by.
 * @param {number|null} [limitResults=null] Max results to return in the array (null/0 for all). Fetches one extra to check if more exist.
 * @param {string|null} [userId=null] The ID of the current user to determine 'learned' status.
 * @returns {Promise<{results: Array, hasMore: boolean}>} A promise resolving with results and hasMore flag.
 */
export const searchItems = async (searchQuery, searchType, searchBy = 'kana', limitResults = null, userId = null, learnedIdsSet = null) => {
  if (!searchQuery || typeof searchQuery !== 'string' || searchQuery.trim().length === 0) {
    return { results: [], hasMore: false };
  }
  const trimmedQuery = searchQuery.trim();
  let hasMore = false;

  try {
    const collectionToSearch = searchType === 'words' ? 'words' : 'sentences';
    let baseQueryRef;

    if (searchType === 'words') {
      if (searchBy === 'kanji') {
        // Search by the 'word' field for Kanji
        baseQueryRef = query(collection(firestore, collectionToSearch), orderBy('word'), startAt(trimmedQuery), endAt(trimmedQuery + '\uf8ff'));
      } else {
        // Default to 'kana'
        baseQueryRef = query(collection(firestore, collectionToSearch), orderBy('kana'), startAt(trimmedQuery), endAt(trimmedQuery + '\uf8ff'));
      }
    } else {
      // searchType is 'sentences'
      if (searchBy === 'kanji') {
        // Search by the 'sentence' field for Kanji
        const orderByField = 'sentence';
        baseQueryRef = query(collection(firestore, collectionToSearch), orderBy(orderByField), startAt(trimmedQuery), endAt(trimmedQuery + '\uf8ff'));
      } else {
        // Default to 'kana' for sentences
        const orderByField = 'sentence_kana';
        baseQueryRef = query(collection(firestore, collectionToSearch), orderBy(orderByField), startAt(trimmedQuery), endAt(trimmedQuery + '\uf8ff'));
      }
    }

    // --- Apply limit & Execute Query ---

    let finalQueryRef = baseQueryRef;
    let actualLimit = limitResults;
    if (actualLimit && actualLimit > 0) {
      finalQueryRef = query(baseQueryRef, limit(actualLimit + 1));
    }
    const snapshot = await getDocs(finalQueryRef); // --- Slice Docs & Check hasMore (No changes here) ---

    let finalDocs = snapshot.docs;
    if (actualLimit && actualLimit > 0 && snapshot.docs.length > actualLimit) {
      hasMore = true;
      finalDocs = snapshot.docs.slice(0, actualLimit);
    }

    // --- Prepare and Enrich Results ---

    const rawResults = finalDocs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
      };
    });

    // Enrich the raw results with user-specific data (learned status AND srs_stage)

    const enrichedResults = await enrichItemsWithUserData(rawResults, userId, searchType, learnedIdsSet);

    return { results: enrichedResults, hasMore };
  } catch (error) {
    console.error(`Error searching ${searchType} by ${searchBy}:`, error);
    return { results: [], hasMore: false };
  }
};
